import React, { useState, useEffect, useRef, useCallback } from 'react';
import './FeedbackModal.css';
import {
  popoverContainerStyle,
  popoverButtonContainerStyle,
  getCancelButtonStyle,
  getSubmitButtonStyle
} from './PopoverStyles';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Feedback Modal Component
 * Issue #232: Add "Feedback senden" option in profile page
 *
 * Features:
 * - NPS score selection (1-10, mandatory)
 * - Optional feedback text
 * - Selected NPS button highlighted with brand color
 * - German and English support
 * - Thank you message after submission
 * - Accessible keyboard navigation
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {Function} onClose - Close callback
 * @param {Function} onSubmit - Submit callback (feedback) => Promise
 * @param {boolean} isSubmitting - Whether submission is in progress
 */
const FeedbackModal = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false
}) => {
  const { t } = useLanguage();

  // Form state
  const [npsScore, setNpsScore] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [error, setError] = useState('');
  const [showThankYou, setShowThankYou] = useState(false);

  const modalRef = useRef(null);
  const firstButtonRef = useRef(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setNpsScore(null);
      setFeedbackText('');
      setError('');
      setShowThankYou(false);
      // Focus first NPS button after modal opens
      setTimeout(() => firstButtonRef.current?.focus(), 10);
    }
  }, [isOpen]);

  // Handle keyboard navigation and focus trap
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;

    if (e.key === 'Escape' && !isSubmitting && !showThankYou) {
      e.preventDefault();
      onClose();
      return;
    }

    // Focus trap
    if (e.key === 'Tab') {
      const focusableElements = modalRef.current?.querySelectorAll(
        'button:not([disabled]), textarea:not([disabled])'
      );

      if (!focusableElements || focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }
  }, [isOpen, isSubmitting, showThankYou, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  // Handle NPS score selection
  const handleNpsSelect = (score) => {
    setNpsScore(score);
    setError('');
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validate NPS score (mandatory)
    if (npsScore === null) {
      setError(t('feedback.npsRequired') || 'Bitte wähle eine Bewertung aus.');
      return;
    }

    const feedback = {
      nps_score: npsScore,
      feedback_text: feedbackText.trim() || null
    };

    try {
      await onSubmit(feedback);
      setShowThankYou(true);
      // Auto-close after showing thank you message
      setTimeout(() => {
        setShowThankYou(false);
        onClose();
      }, 2500);
    } catch (error) {
      // Error handled by parent component
    }
  };

  // Handle thank you modal close
  const handleThankYouClose = () => {
    setShowThankYou(false);
    onClose();
  };

  if (!isOpen) return null;

  // Show thank you message
  if (showThankYou) {
    return (
      <div
        className="modal-overlay"
        onClick={handleThankYouClose}
        role="presentation"
      >
        <div
          className="feedback-thank-you-modal"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="feedback-thank-you-title"
        >
          <div className="feedback-thank-you-content">
            <span className="feedback-thank-you-icon" aria-hidden="true">&#127881;</span>
            <p id="feedback-thank-you-title" className="feedback-thank-you-text">
              {t('feedback.thankYou') || 'Vielen Dank für dein Feedback! Das hilft uns, die App zu verbessern.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="modal-overlay"
      onClick={isSubmitting ? undefined : onClose}
      role="presentation"
    >
      <div
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        style={{ ...popoverContainerStyle, maxHeight: '90vh', overflow: 'auto' }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-modal-title"
      >
        {/* Header with feedback icon and title */}
        <div className="feedback-header">
          <div className="feedback-title-group">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="feedback-icon">
              <path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8 9H16M8 13H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span id="feedback-modal-title" className="feedback-title">
              {t('feedback.title') || 'Feedback senden'}
            </span>
          </div>
          <button
            className="feedback-close-button"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label={t('close') || 'Schließen'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* NPS Question */}
        <p className="feedback-nps-question">
          {t('feedback.npsQuestion') || 'Wie wahrscheinlich ist es, dass du UNIVERSE einem Freund empfiehlst?'}
          <span className="required-marker" aria-hidden="true"> *</span>
        </p>

        {/* NPS Scale 1-10 */}
        <div className="feedback-nps-scale" role="group" aria-label={t('feedback.npsScaleLabel') || 'Bewertungsskala 1 bis 10'}>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((score) => (
            <button
              key={score}
              ref={score === 1 ? firstButtonRef : null}
              type="button"
              className={`feedback-nps-button ${npsScore === score ? 'selected' : ''}`}
              onClick={() => handleNpsSelect(score)}
              disabled={isSubmitting}
              aria-pressed={npsScore === score}
              aria-label={`${score} ${t('feedback.of10') || 'von 10'}`}
            >
              {score}
            </button>
          ))}
        </div>

        {/* Scale labels */}
        <div className="feedback-nps-labels">
          <span className="feedback-nps-label-left">{t('feedback.unlikely') || '1=unwahrscheinlich'}</span>
          <span className="feedback-nps-label-right">{t('feedback.veryLikely') || '10=sehr wahrscheinlich'}</span>
        </div>

        {/* Error message */}
        {error && (
          <p className="feedback-error" role="alert">
            {error}
          </p>
        )}

        {/* Feedback text label */}
        <label className="feedback-text-label" htmlFor="feedback-text">
          {t('feedback.feedbackLabel') || 'Dein Feedback (optional)'}
        </label>

        {/* Feedback text input */}
        <textarea
          id="feedback-text"
          className="feedback-text-input"
          value={feedbackText}
          onChange={(e) => {
            setFeedbackText(e.target.value);
            // Auto-resize textarea
            e.target.style.height = 'auto';
            e.target.style.height = e.target.scrollHeight + 'px';
          }}
          placeholder={t('feedback.feedbackPlaceholder') || 'Teile uns deine Gedanken mit...'}
          disabled={isSubmitting}
          rows={3}
          style={{ overflow: 'hidden', resize: 'none' }}
          aria-describedby="feedback-text-hint"
        />
        <span id="feedback-text-hint" className="sr-only">
          {t('feedback.feedbackOptionalHint') || 'Dieses Feld ist optional'}
        </span>

        {/* Buttons */}
        <div style={popoverButtonContainerStyle}>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            style={getCancelButtonStyle(isSubmitting)}
          >
            {t('cancel') || 'Abbrechen'}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={getSubmitButtonStyle(isSubmitting)}
          >
            {isSubmitting ? (t('feedback.submitting') || 'Wird gesendet...') : (t('feedback.submit') || 'Absenden')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackModal;
